import { useState, useRef, useEffect } from "react";
import { ROUTES } from "../routes/Routes";
import { useResponsive } from "../helper/useResponsive";
import { AppConstant } from "../constant/AppConstant";
import { getCategoryName, getProductLink, getSlugValue, } from "../helper/utility";
import TrackOrderDialog from "./TrackOrderDialog";
import { PageModel } from "../models/PageModel";
import { BrandModel } from "../models/BrandModel";
import { VehicleTypeModel } from "../models/VehicleTypeModel";
import { StateModel } from "../models/StateModel";
import locationIcon from "../assets/icons/ic_track_order.png";
import noImage from "../assets/icons/logo.png";
import unselectedDownArrow from "../assets/icons/unselected_down_arrow.svg";
import selectedUpArrow from "../assets/icons/selected_up_arrow.svg";
import unselectRightArrow from "../assets/icons/unselect_right_arrow.svg";
import selectRightArrow from "../assets/icons/select_right_arrow.svg";
import vehicleIcon from "../assets/icons/vehicle_icon.svg";
import closeCircle from "../assets/icons/close_circle.png";
import "../assets/css/HeaderMenu.css";

interface MegaMenuProps {
    navigate: any;
    mobileMenuOpen: boolean;
    activeMenu: number | null;
    setActiveMenu: (index: number | null) => void;
    dynamicServices: PageModel[];
    setMobileMenuOpen: (open: boolean) => void;
    brands: BrandModel[];
    vehicleTypes: VehicleTypeModel[];
    // states: StateModel[];
}

type SubMenuItem = {
    title: string;
    link?: string;
    onlyForTyre?: boolean;
    id?: string;
    image?: string;
    subMenu?: SubMenuItem[];
};

type MenuItem = {
    title: string;
    link?: string;
    subMenu?: SubMenuItem[];
};

const MegaMenu = ({
    navigate,
    mobileMenuOpen,
    activeMenu,
    setActiveMenu,
    dynamicServices,
    setMobileMenuOpen,
    brands,
    vehicleTypes,
    // states
}: MegaMenuProps) => {
    const { isMobile } = useResponsive();
    const [selectedCategory, setSelectedCategory] = useState(1);
    const [activeSubMenuIndex, setActiveSubMenuIndex] = useState<number | null>(isMobile ? null : 0);
    const [activeSubMenuIndexHover, setActiveSubMenuIndexHover] = useState<number>(0);
    const [dynamicServiceList, setDynamicServiceList] = useState<{ title: string; link: string }[]>([]);
    const [brandMenu, setBrandMenu] = useState<{ id: string; title: string; image: string; link: string; }[]>([]);
    const [vehicleMenu, setVehicleMenu] = useState<{ id: string; title: string; link: string; }[]>([]);
    const [locationMenu, setLocationMenu] = useState<{ title: string; link: string; }[]>([]);
    
    const menuRef = useRef<HTMLDivElement>(null);

    const menuItemList = [
        {
            title: "Shop by brands",
            ...(brandMenu.length > 0 && { subMenu: brandMenu }),
        },
        {
            title: "Shop by vehicle",
            ...(vehicleMenu.length > 0 && { subMenu: vehicleMenu }),
        },
        { title: "Shop by size", onlyForTyre: true, link: ROUTES.HOME.path },
    ];

    const staticServices = [
        {
            title: "Tyre Sales",
            key: "tyre-sale",
            link: ROUTES.MOBILE_OR_DYNAMIC_SERVICES.path.replace(":param", "tyre-sale"),
        },
        {
            title: "Onsite Fitting",
            key: "onsite-fitting",
            link: ROUTES.MOBILE_OR_DYNAMIC_SERVICES.path.replace(":param", "onsite-fitting"),
        },
        {
            title: "Puncture Repair",
            key: "puncture-repair",
            link: ROUTES.MOBILE_OR_DYNAMIC_SERVICES.path.replace(":param", "puncture-repair"),
        },
        {
            title: "Tyre Rotation",
            key: "tyre-rotation",
            link: ROUTES.MOBILE_OR_DYNAMIC_SERVICES.path.replace(":param", "tyre-rotation"),
        },
        {
            title: "Wheel Balancing",
            key: "wheel-balancing",
            link: ROUTES.MOBILE_OR_DYNAMIC_SERVICES.path.replace(":param", "wheel-balancing"),
        },
    ];

    const menuData: MenuItem[] = [
        { title: "Home", link: ROUTES.HOME.path },
        { title: "Tyre", ...(menuItemList.length > 0 && { subMenu: menuItemList }) },
        { title: "Wheel", ...(menuItemList.length > 0 && { subMenu: menuItemList }) },
        ...(AppConstant.isBatteryShow
            ? [
                {
                    title: "Battery",
                    ...(menuItemList.length > 0 && { subMenu: menuItemList.filter(item => !item.onlyForTyre) }),
                }
            ]
            : []
        ),
        {
            title: "Combo",
            link: getProductLink(getSlugValue(getCategoryName(4) ?? ""), "", ""),
        },
        // { title: "Location", ...(locationMenu.length > 0 && { subMenu: locationMenu }) },
        {
            title: "Services",
            subMenu: [...staticServices, ...dynamicServiceList].map((service) => ({
                title: service.title,
                link: service.link,
            })),
        },
        { title: "Blog", link: ROUTES.BLOG.path },
        { title: "About Us", link: ROUTES.ABOUT_US.path },
        { title: "Contact Us", link: ROUTES.CONTACT_US.path },
        { title: "Feedback", link: ROUTES.FEEDBACK.path },
    ];

    // useEffect(() => {
    //     if (states) {
    //         const stateList = states.map(({ name, _id }) => ({
    //             title: name ?? "",
    //             link: ROUTES.LOCATION.path.replace(":stateId", _id),
    //         }));

    //         setLocationMenu(stateList);
    //     }
    // }, [states]);

    useEffect(() => {
        if (dynamicServices) {
            const dynamicService = dynamicServices.map(
                ({ title, _id }) => ({
                    title: title,
                    link: `${ROUTES.MOBILE_OR_DYNAMIC_SERVICES.path.replace(":param", _id)}`,
                })
            );
            setDynamicServiceList(dynamicService);
        }
    }, [dynamicServices]);

    useEffect(() => {
        if (brands.length) {
            const brandList = brands
                .filter((brand) => brand.type === selectedCategory)
                .map(({ name, _id, image_url }) => {
                    const link = getProductLink(
                        getSlugValue(getCategoryName(selectedCategory) ?? ""),
                        getSlugValue(name), ""
                    );

                    return {
                        id: _id,
                        title: name,
                        image: image_url
                            ? `${AppConstant.IMAGE_BASE_URL}${image_url}`
                            : noImage,
                        link,
                    };
                });

            setBrandMenu(brandList);
        }
    }, [brands, selectedCategory]);

    useEffect(() => {
        if (vehicleTypes.length) {
            const vehicleList = vehicleTypes
                .map(({ name, _id }) => {

                    const vehicleName = name?.toLowerCase().replace("/", "-") ?? "";
                    const link = getProductLink(
                        getSlugValue(getCategoryName(selectedCategory) ?? ""),
                        "", getSlugValue(vehicleName)
                    );

                    return {
                        id: _id,
                        title: name ?? "",
                        link,
                    };
                });

            setVehicleMenu(vehicleList);
        }
    }, [vehicleTypes, selectedCategory]);

    const handleMenuClick = (index: number, hasSubmenu: boolean, link?: string) => {
        if (!hasSubmenu) {
            if (link !== undefined && link !== "") {
                navigate(link);
                setActiveMenu(null);
                setActiveSubMenuIndex(null);
                { isMobile && toggleMobileMenu(); }
            }
        } else {
            if (index >= 1 && index <= 3) {
                setSelectedCategory(index);
            }
            setActiveMenu(activeMenu === index ? null : index);
            setActiveSubMenuIndex(isMobile ? null : 0);
        }
    };

    const handleSubMenuClick = (index: number, hasSubmenu: boolean, link?: string) => {
        const sectionId = "home-search-box";
        if (!hasSubmenu) {
            if (window.location.pathname === link) {
                navigate(ROUTES.HOME.path, { state: { activeTab: selectedCategory, scrollTo: sectionId } });
                setActiveMenu(null);
                setActiveSubMenuIndex(null);
                { isMobile && toggleMobileMenu(); }
                return;
            } else {
                if (link === ROUTES.HOME.path) {
                    navigate(ROUTES.HOME.path, { state: { activeTab: selectedCategory, scrollTo: sectionId } });
                    setActiveMenu(null);
                    setActiveSubMenuIndex(null);
                    { isMobile && toggleMobileMenu(); }
                    return;
                } else {
                    if (link) navigate(link);
                    setActiveMenu(null);
                    setActiveSubMenuIndex(null);
                    { isMobile && toggleMobileMenu(); }
                }
            }
        } else {
            setActiveSubMenuIndex(activeSubMenuIndex === index ? (isMobile ? null : 0) : index);
        }
    };

    const handleSubSubMenuClick = (link?: string) => {
        if (link !== undefined && link !== "") {
            navigate(link);
            setActiveMenu(null);
            setActiveSubMenuIndex(null);
            { isMobile && toggleMobileMenu(); }
        }
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
        setActiveMenu(null);
        setActiveSubMenuIndex(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        if (isMobile) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMobile]);

    return (
        <>
            {!isMobile && (
                <div className="header-mega-menu" ref={menuRef}>
                    <div className="header-mega-menu-content">
                        <div className="header-mega-menu-left">
                            {menuData.map((menu, index) => {
                                const hasSubmenu = (menu.subMenu && menu.subMenu.length > 0) ?? false;
                                const categorySubMenu = index >= 1 && index <= 3
                                return (
                                    <div key={`menu ${index}`} className={`header-mega-menu-item ${activeMenu === index ? "active" : ""}`}
                                        onClick={() => handleMenuClick(index, hasSubmenu, menu.link)}>
                                        <div>
                                            <span>{menu.title}</span>
                                            {hasSubmenu && (
                                                <span className="header-mega-menu-item-img"
                                                    style={{
                                                        backgroundImage: `url(${activeMenu === index ? selectedUpArrow : unselectedDownArrow})`,
                                                    }}
                                                />
                                            )}
                                        </div>
                                        {activeMenu === index && hasSubmenu && (
                                            <div className="header-mega-submenu">
                                                <div className="header-mega-submenu-item">
                                                    <div className={categorySubMenu ? "header-mega-submenu-left" : "header-mega-submenu-grid"}>
                                                        {categorySubMenu &&
                                                            <span className="header-mega-submenu-left-title">{menu.title}</span>
                                                        }
                                                        {menu.subMenu?.map((subItem, subIndex) => {
                                                            const hasSubSubMenu = (subItem.subMenu && subItem.subMenu.length > 0) ?? false;
                                                            const isActiveTab = activeSubMenuIndex === subIndex;
                                                            return (
                                                                <div key={`submenu ${subIndex}`}>
                                                                    {categorySubMenu ?
                                                                        <div className={`header-mega-submenu-left-item ${isActiveTab ? "header-mega-submenu-left-item-active" : ""}`}
                                                                            onMouseEnter={() => setActiveSubMenuIndexHover(subIndex)}
                                                                            onMouseLeave={() => setActiveSubMenuIndexHover(-1)}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSubMenuClick(subIndex, hasSubSubMenu, subItem.link)
                                                                            }}
                                                                        >
                                                                            <span>{subItem.title}</span>
                                                                            {hasSubSubMenu && (
                                                                                <img src={(activeSubMenuIndex === subIndex || activeSubMenuIndexHover === subIndex) ? selectRightArrow : unselectRightArrow}
                                                                                    alt={subItem.title} className="header-mega-submenu-image"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        : <a href={subItem.link} className="header-mega-submenu-grid-item"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handleSubMenuClick(subIndex, hasSubSubMenu, subItem.link)
                                                                            }}>
                                                                            <span>{subItem.title}</span>
                                                                        </a>
                                                                    }
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                    <div className="header-mega-submenu-right">
                                                        {(() => {
                                                            const subSubMenu =
                                                                menu.subMenu?.[activeSubMenuIndex!!]?.subMenu ?? [];

                                                            if (!subSubMenu.length) return null;

                                                            return subSubMenu.map((childItem, childIndex) => (
                                                                <a href={childItem.link}
                                                                    key={`subsub-${index}-${childIndex}`}
                                                                    className={`header-mega-subsubmenu-item ${!childItem.image ? "vehicle-item" : ""}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleSubSubMenuClick(childItem.link);
                                                                    }}>
                                                                    {childItem.image ? (
                                                                        <img
                                                                            src={childItem.image}
                                                                            alt={childItem.title}
                                                                            className="header-mega-subsubmenu-image"
                                                                        />
                                                                    ) : (
                                                                        <div className="menu-vehicle">
                                                                            <img src={vehicleIcon} alt="vehicle" className="menu-vehicle-icon" />
                                                                            <span className="menu-vehicle-text">{childItem.title}</span>
                                                                        </div>
                                                                    )}
                                                                </a>

                                                            ));
                                                        })()}

                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        <div className="header-mega-menu-right" onClick={() => { TrackOrderDialog.show() }}>
                            <h5>Track Order</h5>
                            <img src={locationIcon} alt="location" />
                        </div>
                    </div>
                </div>
            )}

            {isMobile && (
                <>
                    <div className={`header-mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
                        {menuData.map((menu, index) => {
                            const hasSubmenu = (menu.subMenu && menu.subMenu.length > 0) ?? false;
                            return (
                                <div key={`mobile-menu-${index}`} className="header-mobile-menu-item">
                                    <div
                                        className={activeMenu === index ? "header-mobile-menu-title active" : "header-mobile-menu-title"}
                                        onClick={() => handleMenuClick(index, hasSubmenu, menu.link)}
                                    >
                                        <span>{menu.title}</span>
                                        {hasSubmenu && (
                                            <img
                                                src={activeMenu === index ? selectedUpArrow : unselectedDownArrow}
                                                alt=""
                                                className="header-mobile-arrow"
                                                style={{
                                                    filter: activeMenu === index ? "brightness(0) invert(0.2)" : "none",
                                                }}
                                            />
                                        )}
                                    </div>

                                    {activeMenu === index && hasSubmenu && (
                                        <div className="header-mobile-submenu">
                                            {menu.subMenu?.map((subItem, subIndex) => {
                                                const hasSubSubmenu = (subItem.subMenu && subItem.subMenu.length > 0) ?? false;
                                                return (
                                                    <div key={`mobile-sub-${subIndex}`}>
                                                        <a href={subItem.link} className={activeSubMenuIndex === subIndex ? "header-mobile-submenu-title active" : "header-mobile-submenu-title"}
                                                            onClick={() => handleSubMenuClick(subIndex, hasSubSubmenu, subItem.link)}>
                                                            <span>{subItem.title}</span>
                                                            {hasSubSubmenu && (
                                                                <img
                                                                    src={activeSubMenuIndex === subIndex ? selectedUpArrow : unselectedDownArrow}
                                                                    alt=""
                                                                    className="header-mobile-arrow"
                                                                />
                                                            )}
                                                        </a>

                                                        {hasSubSubmenu &&
                                                            activeSubMenuIndex === subIndex && (
                                                                <div className="header-mobile-subsubmenu">
                                                                    {subItem.subMenu!!.map(
                                                                        (subSubItem, subSubIndex) => (
                                                                            <a
                                                                                href={subSubItem.link}
                                                                                key={`mobile-subsub-${subSubIndex}`}
                                                                                className={`header-mobile-subsubmenu-item ${!subSubItem.image ? "vehicle-item" : ""}`}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    navigate(subSubItem.link);
                                                                                    toggleMobileMenu();
                                                                                }}
                                                                            >
                                                                                {subSubItem.image ? (
                                                                                    <img className="header-mega-subsubmenu-image"
                                                                                        src={subSubItem.image}
                                                                                        alt={subSubItem.title}
                                                                                    />
                                                                                ) : (
                                                                                    <div className="menu-vehicle">
                                                                                        <img src={vehicleIcon} alt="vehicle" className="menu-vehicle-icon" />
                                                                                        <span className="menu-vehicle-text">{subSubItem.title}</span>
                                                                                    </div>
                                                                                )}

                                                                            </a>
                                                                        )
                                                                    )}
                                                                </div>
                                                            )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {
                        mobileMenuOpen && (
                            <div className={`header-mobile-extra ${mobileMenuOpen ? "open" : ""}`} onClick={toggleMobileMenu}>
                                <img src={closeCircle}
                                    className="header-mobile-menu-close"
                                    onClick={toggleMobileMenu}
                                />
                            </div>
                        )
                    }
                </>
            )}
        </>

    );
};

export default MegaMenu;
